import { useMemo } from 'react';
import { z } from 'zod';
import { IPasswordValidation } from 'src/shared/interface/forgot-password';
import { PASSWORD_CONFIG, ERROR_MESSAGES } from 'src/shared/constants';

interface IUsePasswordValidationReturn {
  validation: IPasswordValidation;
  isValid: boolean;
  errors: string[];
}

interface IUsePasswordValidationOptions {
  currentPassword?: string;
  skipMatchCheck?: boolean;
}

export const createPasswordSchema = (currentPassword?: string) => {
  return z
    .string()
    .min(1, ERROR_MESSAGES.REQUIRED_NEW_PASSWORD)
    .transform((val) => val.trim())
    .refine((val) => val.length >= PASSWORD_CONFIG.MIN_LENGTH, {
      message: ERROR_MESSAGES.MIN_LENGTH,
    })
    .refine((val) => PASSWORD_CONFIG.REGEX.DIGIT.test(val), {
      message: ERROR_MESSAGES.MISSING_DIGIT,
    })
    .refine((val) => PASSWORD_CONFIG.REGEX.LOWERCASE.test(val), {
      message: ERROR_MESSAGES.MISSING_LOWERCASE,
    })
    .refine((val) => PASSWORD_CONFIG.REGEX.UPPERCASE.test(val), {
      message: ERROR_MESSAGES.MISSING_UPPERCASE,
    })
    .refine((val) => PASSWORD_CONFIG.REGEX.SPECIAL_CHAR.test(val), {
      message: ERROR_MESSAGES.MISSING_SPECIAL_CHAR,
    })
    .refine((val) => !currentPassword || val !== currentPassword.trim(), {
      message: ERROR_MESSAGES.SAME_AS_CURRENT,
    });
};

export const usePasswordValidation = (
  password: string,
  confirmPassword: string,
  options?: IUsePasswordValidationOptions,
): IUsePasswordValidationReturn => {
  const { currentPassword, skipMatchCheck = false } = options || {};

  // Validation state chi tiết cho từng điều kiện
  const validation = useMemo<IPasswordValidation>(() => {
    const trimmedPassword = password.trim();

    return {
      minLength: trimmedPassword.length >= PASSWORD_CONFIG.MIN_LENGTH,
      hasNumber: PASSWORD_CONFIG.REGEX.DIGIT.test(trimmedPassword),
      hasLowerCase: PASSWORD_CONFIG.REGEX.LOWERCASE.test(trimmedPassword),
      hasUpperCase: PASSWORD_CONFIG.REGEX.UPPERCASE.test(trimmedPassword),
      hasSpecialChar: PASSWORD_CONFIG.REGEX.SPECIAL_CHAR.test(trimmedPassword),
      isMatch: skipMatchCheck
        ? true
        : password.length > 0 && password.trim() === confirmPassword.trim(),
    };
  }, [password, confirmPassword, skipMatchCheck]);

  const errors = useMemo<string[]>(() => {
    if (!password) return [];

    const passwordSchema = createPasswordSchema(currentPassword);
    const result = passwordSchema.safeParse(password);

    if (!result.success) {
      return result.error.issues.map((issue) => issue.message);
    }

    // Kiểm tra password match nếu cần
    if (!skipMatchCheck && confirmPassword) {
      const trimmedPassword = password.trim();
      const trimmedConfirm = confirmPassword.trim();

      if (trimmedPassword !== trimmedConfirm) {
        return [ERROR_MESSAGES.CONFIRM_NOT_MATCH];
      }
    }

    return [];
  }, [password, confirmPassword, currentPassword, skipMatchCheck]);

  const isValid = useMemo(() => {
    return (
      validation.minLength &&
      validation.hasNumber &&
      validation.hasLowerCase &&
      validation.hasUpperCase &&
      validation.hasSpecialChar &&
      validation.isMatch &&
      errors.length === 0
    );
  }, [validation, errors]);

  return {
    validation,
    isValid,
    errors,
  };
};
